import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";
import { createRow, updateRow } from "./supabaseRealtime";

/* La documentación de los choferes: leer, corregir, y el PIN aparte.
 *
 * DE DÓNDE SALE. `chofer_documentos` en Supabase, volcada desde la planilla del
 * Drive que mantiene documentación. La planilla sigue siendo la fuente mientras
 * se use; lo que cambia es que ahora TAMBIÉN se puede corregir desde acá, que
 * hasta hoy sólo se podía por WhatsApp.
 *
 * Y lo que se corrige no se pierde en la próxima sincronización: el vuelco usa
 * `coalesce`, o sea que completa huecos y no pisa lo que ya tiene valor. Al
 * revés, cada sync borraría las correcciones y el síntoma sería "los datos se
 * desarreglan solos".
 */

const TABLA = "chofer_documentos";

export async function traerDocumentacion() {
  return traerTodas(() =>
    supabase
      .from(TABLA)
      // EL PIN NO ESTÁ EN ESTA CONSULTA, y no es un olvido: vive en otra tabla
      // con su propia RLS. Se pide con `traerPins`, que sin el permiso vuelve
      // vacía — la que decide es la base, no la pantalla.
      .select(
        "dni,nombre,empresa,telefono,zona,anotaciones," +
          "registro_vence,psicofisico_vence,peligrosas_vence," +
          "requiere_peligrosas,tipo_servicio,activo,direccion," +
          "usuario,usuario_nombre,actualizado_en",
        { count: "exact" },
      )
      // El DNI es la clave de la tabla: alcanza para que el orden no empate.
      .order("dni", { ascending: true }),
  );
}

/* La documentación de UNA persona, para su ficha.
 *
 * Es su propia consulta y no un `find` sobre `traerDocumentacion()`: aquélla
 * trae 2.000 filas para dibujar la tabla entera, y la ficha necesita una. Pedir
 * las 2.000 para mostrar un renglón es lo que hace que abrir una ficha se
 * sienta lento sin que nada esté roto.
 *
 * `null` cuando esa persona no está en la planilla — que es lo normal: son 224
 * choferes sobre 2.476 personas del padrón. NO es un error, y la pantalla lo
 * dice con esas palabras.
 */
export async function documentacionDe(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  if (!d) return null;
  const { data, error } = await supabase
    .from(TABLA)
    .select(
      "dni,nombre,empresa,telefono,zona,anotaciones," +
        "registro_vence,psicofisico_vence,peligrosas_vence," +
        "requiere_peligrosas,tipo_servicio,activo,direccion," +
        "usuario,usuario_nombre,actualizado_en",
    )
    .eq("dni", d)
    .limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

/* Pasa por `updateRow` y no por un update propio: es el cuello donde se sella
 * quién cargó. Corregir un vencimiento es exactamente el dato que después
 * alguien va a querer saber quién lo tocó. */
export const corregirDocumento = (dni, cambios) =>
  updateRow(
    TABLA,
    dni,
    { ...cambios, actualizado_en: new Date().toISOString() },
    // La clave de esta tabla es `dni` y no `id`, que es el default.
    // Sin esto, PostgREST rechaza la fila entera — y era el motivo por
    // el que ninguna corrección desde esta pantalla entraba.
    "dni",
  );

/* Lo mismo, a varios de una.
 *
 * ── SE VA DE A UNA FILA A PROPÓSITO ──────────────────────────────────────
 *
 * Un `update ... in (43 dni)` sería una sola consulta y más rápido, y no se
 * hace: pasar por `corregirDocumento` es lo que garantiza que las 43 filas
 * queden **selladas con quién las tocó**, igual que si se hubieran editado a
 * mano. `updateRow` es el cuello por donde pasa toda la escritura del hub, y
 * una escritura masiva que lo esquive nace sin autor — que es exactamente el
 * dato que uno va a querer cuando aparezcan 43 filas cambiadas.
 *
 * Son 43 requests de milisegundos contra una tabla de 224 filas. El costo es
 * real y es chico; el de no saber quién escribió no se repone.
 *
 * ── Y SI FALLA A LA MITAD, SE DICE CUÁNTAS ENTRARON ──────────────────────
 *
 * No corta en el primer error: sigue con las demás y devuelve las dos listas.
 * Un «no se pudo» sobre una operación que escribió 30 de 43 es peor que un
 * error — manda a repetirla entera sin decir que ya hay 30 hechas.
 */
export async function corregirVarios(dnis, cambios) {
  const ok = [];
  const fallaron = [];
  for (const dni of dnis || []) {
    try {
      await corregirDocumento(dni, cambios);
      ok.push(dni);
    } catch (e) {
      fallaron.push({ dni, error: e?.message || String(e) });
    }
  }
  return { ok, fallaron };
}

/* Dar de baja a un chofer, y volver a darle de alta.
 *
 * ── NO BORRA NADA ────────────────────────────────────────────────────────
 *
 * Escribe una columna y nada más: la fila queda entera, con sus vencimientos y
 * con quién los cargó. Es el mismo criterio que anular un movimiento y que
 * cancelar un ticket de taller — que alguien haya dejado de manejar es parte de
 * la historia, y borrar la fila haría desaparecer por qué esos papeles están
 * como están.
 *
 * ── ES LA MISMA PUERTA QUE UNA CORRECCIÓN ────────────────────────────────
 *
 * Pasa por `corregirDocumento`, así que queda **sellada con quién la dio de
 * baja** y con la fecha. Un `update` propio sería una línea más corta y una
 * baja anónima: la pregunta «¿quién lo sacó de la lista?» es exactamente la que
 * aparece el día que alguien no recibe un aviso que esperaba.
 */
export const cambiarActivo = (dni, activo) =>
  corregirDocumento(dni, { activo: Boolean(activo) });

/* Dar de alta un chofer.
 *
 * PASA POR `createRow` con la clave `dni`, no por un insert propio: es el
 * cuello donde se sella quién lo cargó. Un alta sin autor es justo la fila de
 * la que después uno quiere saber quién la creó.
 *
 * LA CLAVE VA COMO CUARTO ARGUMENTO porque el default es `id`, que esta tabla
 * no tiene — y ése fue el bug que hizo que esta pantalla nunca escribiera nada.
 */
export const crearChofer = (fila) => {
  const { dni, ...resto } = fila;
  return createRow(
    TABLA,
    dni,
    { ...resto, actualizado_en: new Date().toISOString() },
    "dni",
  );
};

/* ¿Ese DNI ya está? Lo pregunta el alta antes de guardar.
 *
 * DEVUELVE `true` / `false` / `null`, y el tercero importa: `null` es «no pude
 * fijarme», y ahí el alta no dice que está limpio — dice que no se pudo
 * verificar, que es otra cosa. Misma regla que `duplicados`.
 */
export async function existeChofer(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  if (!d) return false;
  const { data, error } = await supabase
    .from(TABLA)
    .select("dni")
    .eq("dni", d)
    .limit(1);
  if (error) return null;
  return (data || []).length > 0;
}

/* Quién es ese DNI según el padrón de gente.
 *
 * Se usa para OFRECER el nombre, no para exigirlo: hay 13 DNI en documentación
 * que el padrón no tiene, y son gente real con papeles cargados.
 */
export async function personaDelPadron(dni) {
  const d = String(dni ?? "").replace(/\D/g, "");
  if (!d) return null;
  const { data, error } = await supabase
    .from("personas")
    .select("id, nombre, apellido")
    .eq("id", d)
    .maybeSingle();
  if (error || !data) return null;
  const nombre = [data.apellido, data.nombre].filter(Boolean).join(", ");
  return { dni: d, nombre: nombre.replace(/^,\s*/, "") || null };
}

/* El teléfono que la planilla tiene de UNA persona.
 *
 * POR QUÉ EXISTE. El padrón guarda `personas.telefono` y casi nunca lo tiene:
 * de 2.476 filas, 65 con un número que se pueda entender. La planilla que
 * mantiene Marcos tiene 214 de 224, y desde que se sincroniza sola a las 03:50
 * está acá adentro — o sea que el dato YA ESTABA en el hub y la ficha de la
 * persona no lo mostraba. Cruzadas por DNI, son **176 personas que ganan un
 * teléfono** que antes había que ir a buscar a otra pantalla.
 *
 * Se pide de a una y no la tabla entera: la ficha abre una persona por vez, y
 * traer 224 filas para leer una es repartir teléfonos que nadie pidió.
 *
 * Devuelve `""` cuando no hay fila y `null` cuando NO SE PUDO MIRAR, y quien
 * llama las dice distinto. Un error de red dibujado como «no tiene teléfono»
 * manda a cargar un dato que ya está.
 */
export async function telefonoDeChofer(dni) {
  if (!dni) return "";
  const { data, error } = await supabase
    .from(TABLA)
    .select("telefono")
    .eq("dni", String(dni))
    .maybeSingle();
  if (error) return null;
  return data?.telefono || "";
}

/* TODOS LOS PIN DE UNA, y esto cambió a pedido.
 *
 * Antes se pedían de a uno con un ojito, con el argumento de que traer 210
 * credenciales para usar una es repartirlas. El argumento vale en general y
 * acá no: **el PIN sin la tarjeta física no sirve para nada**, y el que lo
 * necesita lo necesita al lado del teléfono de la persona, no a un clic por
 * fila.
 *
 * LO QUE NO CAMBIÓ ES LO QUE DE VERDAD RESTRINGE: la RLS de `chofer_pin` exige
 * `ver.pin_ypf` del lado de la BASE. Sin el permiso esta consulta devuelve una
 * lista vacía —no un error— aunque se la llame desde la consola del navegador.
 * Por eso se puede pedir la tabla entera sin abrir nada.
 *
 * Devuelve un mapa `dni → pin`. Vacío puede ser «no hay ninguno cargado» o «no
 * los podés ver», y quien llama ya sabe cuál por el permiso — son dos cosas
 * distintas y la pantalla las dice distinto.
 */
export async function traerPins() {
  const { data, error } = await supabase.from("chofer_pin").select("dni, pin");
  if (error) throw error;
  const mapa = {};
  for (const f of data || []) mapa[String(f.dni)] = f.pin;
  return mapa;
}
